'use client'

import { useState, useMemo } from 'react'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { atomDark, oneLight } from 'react-syntax-highlighter/dist/cjs/styles/prism'
import { useTheme } from 'next-themes'
import { useLocale } from 'next-intl'
import { Github, Copy, Check } from 'lucide-react'
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard'

/**
 * Live-run demos section.
 *
 * Each tab pairs code based on a script in framerslab/agentos/examples
 * with output from a run of that code. The spawning and citation tabs run
 * code adapted from their script: their code panels read "adapted from"
 * and their captions say what differs. Tabs that call a model show output
 * captured from one run against the OpenAI API; the mission and bus
 * examples call no model, so every run takes the same steps.
 *
 * Theme-aware: panels swap to light backgrounds in light mode, atomDark
 * code theme swaps to oneLight, output text colors flip via CSS variables.
 */

interface AgentCall {
  agent: string
  input: string
}

interface DemoOutput {
  demoGif?: { src: string; webpSrc?: string; mp4Src?: string; poster?: string; alt: string; width: number; height: number }
  forge?: { agent: string; approved: boolean; comment?: string }
  finalAnswer?: string
  finalAnswerLabel?: string
  finalOverride?: string
  streamPreface?: string
  claims?: { verdict: 'supported' | 'weak' | 'unverifiable'; confidence: number; text: string; source?: string }[]
  dagTiers?: { tier: number; agents: { name: string; durationMs: number }[] }[]
  missionSteps?: { id: string; type: 'gmi' | 'tool'; executor: string }[]
  missionArtifacts?: Record<string, unknown>
  missionConfidence?: number
  busMessages?: { from: string; to: string; status: 'success' | 'error'; preview: string }[]
  corpusStats?: { chunks: number; topics: number; sources: number; platform: Record<string, number> }
  classification?: { tier: number; strategy: string; confidence: number }
  tiersUsed?: number[]
  fallbacksUsed?: string[]
  answer?: string
  sources?: { title: string; uri: string; snippet: string }[]
  agentCalls?: AgentCall[]
  usage: { tokens?: number; cost?: string; latency?: string }
}

interface DemoData {
  id: string
  title: string
  exampleSlug: string
  /** The tab's code changes what the script does, not only its length. */
  adapted?: boolean
  language: string
  code: string
  output: DemoOutput
  caption: React.ReactNode
}

const SINGLE_AGENT_CODE = `import { agent } from '@framers/agentos';

// One agent handles the whole task: instructions, personality, tools and
// session history live inside this single agent(). No team, no hand-off.
// This is the baseline that the next tab upgrades.
const researcher = agent({
  provider: 'openai',
  model: 'gpt-4o',
  instructions:
    'You are a research analyst. Find authoritative sources and ' +
    'write concise prose.',
});

const result = await researcher.generate(
  'Write a 2-paragraph briefing on agentic-AI sandbox security risks. ' +
  'Include a security-audit perspective on node:vm vs container isolation.',
);

console.log(result.text);`

const EMERGENT_CODE = `import { agency } from '@framers/agentos';

const team = agency({
  provider: 'openai',
  model: 'gpt-4o',
  strategy: 'hierarchical',
  instructions:
    'Coordinate a research team. If the task needs a capability ' +
    'your roster does not cover, call spawn_specialist to mint one.',
  agents: {
    researcher: { instructions: 'Find authoritative sources.' },
    writer:     { instructions: 'Produce concise prose.' },
  },
  emergent: {
    enabled: true,         // unlock spawn_specialist tool
    judge:   true,         // LLM judge gates each new spec
    planner: { maxSpecialists: 1, requireJustification: true, maxJudgeCalls: 3 },
  },
  on: {
    emergentForge: (e) => console.log(
      \`[FORGE] spawned "\${e.agentName}" approved=\${e.approved}\`
    ),
  },
});

const result = await team.generate(
  'Write a 2-paragraph briefing on agentic-AI sandbox security risks. ' +
  'Include a security-audit perspective on node:vm vs container isolation. ' +
  'The team has no security auditor; spawn one if needed.',
);

console.log(result.text);`

const STREAMING_CODE = `import { agency } from '@framers/agentos';

const team = agency({
  provider: 'openai',
  strategy: 'sequential',
  agents: {
    researcher: { instructions: 'Gather the most important facts and risks.' },
    writer:     { instructions: 'Turn research into four crisp bullets.' },
  },
  hitl: {
    approvals: { beforeReturn: true },
    handler: async () => ({
      approved: true,
      modifications: {
        output:
          'Approved for delivery:\\n- Rollout risk 1\\n- Rollout risk 2\\n' +
          '- Rollout risk 3\\n- Rollout risk 4',
      },
    }),
  },
});

const stream = team.stream('Summarize the main HTTP/3 rollout risks.');

for await (const chunk of stream.textStream) {
  process.stdout.write(chunk);
}

const finalText = await stream.text;
console.log('\\n--- approved final ---\\n' + finalText);`

const CITATION_CODE = `import { CitationVerifier, formatVerifiedResponse } from '@framers/agentos';

const verifier = new CitationVerifier({
  embedFn: yourEmbeddings,
  supportThreshold:    0.6,  // cosine >= 0.6 = supported
  unverifiableThreshold: 0.3, // cosine <  0.3 = unverifiable
});

const sources = [
  { content: 'Tokyo is the capital and seat of government of Japan.', title: 'Japan Overview' },
  { content: 'The population of Tokyo proper is approximately 14 million.', title: 'Tokyo Demographics' },
];

// Pattern A: pass raw LLM text — verifier decomposes into atomic claims.
// Use when the input is one block of model-generated prose.
const result = await verifier.verify(
  'Tokyo is the capital of Japan. ' +
  'Tokyo proper has roughly 14 million residents. ' +
  'Tokyo hosted the 2020 Summer Olympics in 1457.',
  sources,
);

// Pattern B: pass a pre-decomposed claim array — verifier scores each as-is.
// Use when you've already split prose with your own parser / NER / curated list.
//
// const result = await verifier.verify(
//   [
//     'Tokyo is the capital of Japan.',
//     'Tokyo proper has roughly 14 million residents.',
//     'Tokyo hosted the 2020 Summer Olympics in 1457.',
//   ],
//   sources,
// );

// Or extract first, filter, then verify:
//   const claims = await verifier.extractClaims(llmText);
//   const result = await verifier.verify(claims.filter(c => c.length > 20), sources);

console.log(formatVerifiedResponse(result));
for (const claim of result.claims) {
  console.log(\`  [\${claim.verdict}] (\${claim.confidence}) \${claim.text}\`);
}`

const demos: DemoData[] = [
  {
    id: 'single-agent',
    title: 'One agent, one task',
    exampleSlug: 'examples/single-agent-briefing.mjs',
    language: 'typescript',
    code: SINGLE_AGENT_CODE,
    output: {
      finalAnswer:
        "Agentic AI sandbox environments expose a recurring trade-off between isolation strength and operational cost. Node:vm runs untrusted code inside a fresh V8 context: light to spin up, ergonomic for short LLM-generated snippets, and adequate when the threat model is bugs rather than adversaries. The weakness is that node:vm shares the host Node.js process. Prototype-pollution paths, async-hook escapes, and richly documented context-break primitives mean an attacker with code-execution inside the sandbox can usually reach the host runtime. For a security audit, node:vm is acceptable only when the code's author is trusted and the data inside the sandbox is non-sensitive.\n\nContainer isolation (Docker, gVisor, Firecracker microVMs) adds kernel-level boundaries: namespaces, cgroups, seccomp, and in the gVisor/Firecracker case a separate user-space kernel or hypervisor. The audit win is that even a fully compromised agent inside the container cannot directly read host memory, mount host filesystems, or pivot to other tenants without a kernel-level exploit. The cost is orchestration overhead, image-supply-chain hygiene, and slower cold-start. For production agentic workloads where the runtime executes prompts, tool calls, or forge-generated code from untrusted prompts, container isolation should be the default; node:vm is reserved for trusted-author paths where the threat model permits the weaker boundary.",
      usage: { tokens: 1840 },
    },
    caption: (
      <>
        A single <code className="font-mono text-[var(--color-accent-primary)]">agent()</code> handles the whole brief with its instructions and, when you give it some, its tools. <code className="font-mono text-[var(--color-accent-primary)]">agent()</code> calls the model directly; <code className="font-mono text-[var(--color-accent-primary)]">{`agent({ runtime: 'gmi' })`}</code> runs the same agent on a GMI. The next tab hands the same briefing task to a hierarchical team built with <code className="font-mono text-[var(--color-accent-primary)]">agency()</code>.
      </>
    ),
  },
  {
    id: 'emergent',
    title: 'Spawn an agent at runtime',
    exampleSlug: 'examples/emergent-hierarchical-spawning.mjs',
    adapted: true,
    language: 'typescript',
    code: EMERGENT_CODE,
    output: {
      forge: {
        agent: 'security_auditor',
        approved: true,
        comment: 'EmergentAgentJudge passed; new agent joins the live roster on next turn',
      },
      finalAnswer:
        "Agentic AI systems operating in sandbox environments present unique security challenges, particularly when considering the isolation methods used to contain these systems. Node:vm (Virtual Machines) provide robust isolation as each VM operates with its own OS and resources, offering strong separation from other environments on the same host. This level of isolation helps to contain the potential impact of any security breach within the AI agent itself. However, VMs can be resource-intensive and potentially slower, as they require more overhead to emulate physical hardware, which might influence performance for high-demand AI tasks.\n\nConversely, container-based isolation, such as Docker, offers a more lightweight and flexible approach as containers share the host kernel while isolating the application at the process level. This can be advantageous for deploying numerous, smaller agentic AI instances. However, from a security-audit perspective, containers can be more vulnerable to kernel-level attacks since they share the same underlying OS. The shared kernel can pose risks if one AI agent exploits a vulnerability to affect others. Therefore, when managing agentic AI in sandbox environments, it is crucial to balance the isolation strength of VMs with the efficiency and scalability of containers, while continuously monitoring and patching both systems to mitigate emerging security risks.",
      agentCalls: [
        { agent: 'researcher', input: 'Research security risks associated with agentic AI in sandbox environments...' },
        { agent: 'writer', input: 'Based on the research summary, produce a two-paragraph briefing for a CTO...' },
      ],
      usage: { tokens: 4272 },
    },
    caption: (
      <>
        The code adapts <code className="font-mono text-[var(--color-accent-primary)]">examples/emergent-hierarchical-spawning.mjs</code>: it allows one specialist and gives the team the first tab&apos;s briefing. The team starts with <code className="font-mono text-[var(--color-accent-primary)]">researcher</code> + <code className="font-mono text-[var(--color-accent-primary)]">writer</code>. The prompt asks for a security audit, which neither covers. The manager calls{' '}
        <code className="font-mono text-[var(--color-accent-primary)]">spawn_specialist</code>, EmergentAgentJudge approves the spec, and{' '}
        <code className="font-mono text-[var(--color-accent-primary)]">security_auditor</code> joins the live roster. The final answer below is what GPT-4o produced through that team, captured from a run without edits. Its first paragraph misdescribes <code className="font-mono text-[var(--color-accent-primary)]">node:vm</code>, which runs code in a V8 context inside the Node.js process; Node&apos;s documentation says it is not a security mechanism.
      </>
    ),
  },
  {
    id: 'streaming',
    title: 'Stream tokens, gate the final',
    exampleSlug: 'examples/agency-streaming.mjs',
    language: 'typescript',
    code: STREAMING_CODE,
    output: {
      streamPreface:
        "The rollout of HTTP/3, the latest major version of the Hypertext Transfer Protocol, comes with several risks and challenges. Here are the main ones:\n\n1. Compatibility and Adoption: As a new standard, HTTP/3 may not be immediately supported by all browsers, web servers, and networking equipment...\n\n2. Network Infrastructure: HTTP/3 is built on QUIC, a protocol that runs over UDP. This shift from the traditional TCP protocol may lead to issues with middleboxes like firewalls and routers...\n\n3. Performance Variability: Real-world results can vary based on specific network conditions...\n\n4. Security Concerns: The transition to a new protocol opens up potential new security vulnerabilities, both in the QUIC protocol itself and in its implementations...\n\n[continues streaming through 7 risks total]",
      finalOverride:
        'Approved for delivery:\n- Rollout risk 1\n- Rollout risk 2\n- Rollout risk 3\n- Rollout risk 4',
      usage: { tokens: 923 },
    },
    caption: (
      <>
        The full LLM response streams token-by-token. Right before return, an HITL handler runs and either approves the model output, modifies it, or blocks it. Here the handler swapped the long prose for a tight 4-bullet delivery. <code className="font-mono text-[var(--color-accent-primary)]">stream.text</code> resolves to the approved version; <code className="font-mono text-[var(--color-accent-primary)]">stream.textStream</code> still gives consumers the live tokens.
      </>
    ),
  },
  {
    id: 'citation',
    title: 'Verify every claim against sources',
    exampleSlug: 'examples/citation-verification.mjs',
    adapted: true,
    language: 'typescript',
    code: CITATION_CODE,
    output: {
      // Captured from one run of this tab's code with OpenAI embeddings.
      // The committed example embeds with a mock function, so its scores
      // differ. 2 of 3 claims are supported; the 1457 Olympics claim scores
      // 0.45 against its closest source, between the 0.3 and 0.6
      // thresholds, so it is weak: cosine similarity does not check the date.
      claims: [
        {
          verdict: 'supported',
          confidence: 0.84,
          text: 'Tokyo is the capital of Japan.',
          source: 'Tokyo is the capital and seat of government of Japan.',
        },
        {
          verdict: 'supported',
          confidence: 0.89,
          text: 'Tokyo proper has roughly 14 million residents.',
          source: 'The population of Tokyo proper is approximately 14 million.',
        },
        {
          verdict: 'weak',
          confidence: 0.45,
          text: 'Tokyo hosted the 2020 Summer Olympics in 1457.',
          source: 'Tokyo is the capital and seat of government of Japan.',
        },
      ],
      usage: {},
    },
    caption: (
      <>
        <code className="font-mono text-[var(--color-accent-primary)]">CitationVerifier</code> splits text into claims (with its sentence splitter or your <code className="font-mono text-[var(--color-accent-primary)]">extractClaims</code> callback), scores each claim by cosine similarity against your sources and returns a verdict per claim: supported at 0.6 or above, unverifiable below 0.3, weak in between. An optional NLI function turns a supported verdict into contradicted when the source contradicts the claim. <code className="font-mono text-[var(--color-accent-primary)]">{`agent({ verifyCitations })`}</code> runs the same check after each <code className="font-mono text-[var(--color-accent-primary)]">generate()</code> and attaches the verdicts to <code className="font-mono text-[var(--color-accent-primary)]">result.grounding</code>; it reports them and does not block the answer. The example in the repository embeds with a mock function so it runs without an API key; the scores here come from a run with OpenAI embeddings.
      </>
    ),
  },
  {
    id: 'dag',
    title: 'Multi-agent DAG with dependencies',
    exampleSlug: 'examples/agency-graph.mjs',
    language: 'typescript',
    code: `import { agency } from '@framers/agentos';

const team = agency({
  provider: 'openai',
  strategy: 'graph',  // topo-sorts agents into tiers from dependsOn
  agents: {
    researcher: {
      instructions: 'Gather facts and credible sources. Output a brief.',
    },
    writer: {
      instructions: 'Write a 300-word article from the brief.',
      dependsOn: ['researcher'],
    },
    illustrator: {
      instructions: 'Describe 3 illustrations for the article.',
      dependsOn: ['researcher'],
    },
    reviewer: {
      instructions: 'Final verdict on factual accuracy and consistency.',
      dependsOn: ['writer', 'illustrator'],
    },
  },
});

// Tier 0: researcher
// Tier 1: writer + illustrator (concurrent)
// Tier 2: reviewer
const result = await team.generate(
  'Explain the significance of the James Webb Space Telescope.',
);`,
    output: {
      dagTiers: [
        { tier: 0, agents: [{ name: 'researcher', durationMs: 17015 }] },
        {
          tier: 1,
          agents: [
            { name: 'writer', durationMs: 5232 },
            { name: 'illustrator', durationMs: 2420 },
          ],
        },
        { tier: 2, agents: [{ name: 'reviewer', durationMs: 5017 }] },
      ],
      finalAnswerLabel: "Reviewer's final verdict (gpt-4o)",
      finalAnswer:
        "The article provides a comprehensive overview of the James Webb Space Telescope (JWST), its significance, objectives, and technological advancements. The descriptions of the illustrations effectively complement the article by visually representing JWST's capabilities and missions.\n\nSuggested corrections:\n  • Launch date: the article states December 25, 2021. The launch was on December 25, 2021 UTC; clarifying time zone prevents confusion.\n\nOptional enhancements:\n  • Briefly explain L2 positioning (stability + reduced thermal interference, complementing infrared observations).\n  • Mention MIRI (Mid-Infrared Instrument) to emphasize JWST's full instrument suite.",
      usage: { tokens: 4196 },
    },
    caption: (
      <>
        The graph strategy topologically sorts agents into tiers from <code className="font-mono text-[var(--color-accent-primary)]">dependsOn</code>. <code className="font-mono text-[var(--color-accent-primary)]">generate()</code> runs the agents of a tier concurrently (<code className="font-mono text-[var(--color-accent-primary)]">stream()</code> runs them one after another). Tier 0 runs alone; tier 1 runs both agents in parallel; tier 2 waits for both. Every agent receives the original prompt plus the plain-text outputs of its dependencies, so the reviewer in this run sees the writer&apos;s article and the illustrator&apos;s descriptions before issuing its verdict.
      </>
    ),
  },
  {
    id: 'mission',
    title: 'Plan a mission, run the steps',
    exampleSlug: 'examples/mission-api.mjs',
    language: 'typescript',
    code: `import { GraphRuntime, InMemoryCheckpointStore, mission, toolNode } from '@framers/agentos';
import { z } from 'zod';

// mission() turns a goal into a linear graph of steps from a plan
// template; this goal gets the research template. An anchor splices in
// a step you write yourself: here a fact-check tool after process-info.
const deepResearch = mission('deep-research')
  .input(z.object({ topic: z.string() }))
  .goal('Research {{topic}} thoroughly and produce a cited summary')
  .returns(z.object({
    summary: z.string().optional(),
    confidence: z.number().optional(),
    verified: z.boolean().optional(),
  }))
  .planner({ strategy: 'plan_and_execute', maxSteps: 6 })
  .anchor('fact-check', toolNode('grounding_verifier'), {
    required: true,
    phase: 'validate',
    after: 'process-info',
  })
  .compile();

// The example runs the graph with a stub executor that returns a fixed
// output for each step id, so no model is called. An executor that calls
// your LLM and tools runs the same graph for real.
const runtime = new GraphRuntime({
  checkpointStore: new InMemoryCheckpointStore(),
  nodeExecutor: stubExecutor,  // see the source for the switch on node.id
});

const { steps } = await deepResearch.explain({ topic: 'unified orchestration' });
const artifacts = await runtime.invoke(deepResearch.toIR(), {
  topic: 'unified orchestration',
});`,
    output: {
      missionSteps: [
        { id: 'gather-info', type: 'gmi', executor: 'gmi' },
        { id: 'process-info', type: 'gmi', executor: 'gmi' },
        { id: 'deliver-result', type: 'gmi', executor: 'gmi' },
        { id: 'refine-output', type: 'gmi', executor: 'gmi' },
        { id: 'fact-check', type: 'tool', executor: 'tool' },
      ],
      missionArtifacts: {
        'gather-info': 'gathered research notes',
        'process-info': 'drafted synthesis',
        'fact-check': { verified: true },
        summary:
          'unified orchestration is best explained as a graph runtime layered over deterministic and planner-driven entrypoints.',
        confidence: 0.88,
        verified: true,
        'refine-output': 'handled:refine-output',
      },
      missionConfidence: 0.88,
      usage: {},
    },
    caption: (
      <>
        Same runtime as <code className="font-mono text-[var(--color-accent-primary)]">workflow()</code> and <code className="font-mono text-[var(--color-accent-primary)]">AgentGraph</code>, different authoring surface. <code className="font-mono text-[var(--color-accent-primary)]">mission()</code> builds a linear graph of steps from a plan template (the research template here: gather-info, process-info, deliver-result and refine-output) and splices in anchors such as the fact-check tool. This example&apos;s executor is a stub that returns a fixed output for each step, so the run calls no model. <code className="font-mono text-[var(--color-accent-primary)]">invoke()</code> resolves to the run&apos;s artifacts: the runtime stores each step&apos;s output under the step&apos;s id, except for a step that writes artifacts itself, as deliver-result does here with summary, confidence and verified.
      </>
    ),
  },
  {
    id: 'bus',
    title: 'Inter-agent messaging via the bus',
    exampleSlug: 'examples/agent-communication-bus.mjs',
    language: 'typescript',
    code: `import { AgentCommunicationBus } from '@framers/agentos';

const bus = new AgentCommunicationBus({
  routingConfig: { enableRoleRouting: true, enableLoadBalancing: true },
});

// Agents join an agency under a role the bus can route on.
bus.registerAgent('coordinator-gmi', 'agency-docs', 'coordinator');
bus.registerAgent('researcher-gmi',  'agency-docs', 'researcher');
bus.registerAgent('writer-gmi',      'agency-docs', 'writer');

// An agent answers through its subscription; a request that nobody
// answers waits out its timeout.
bus.subscribe('researcher-gmi', async (message) => {
  if (message.type !== 'question') return;
  await bus.sendToAgent(message.fromAgentId, {
    type: 'answer',
    fromAgentId: 'researcher-gmi',
    content: { findings: ['The graph runtime should stay shared across all three authoring APIs.' /* , ... */] },
    inReplyTo: message.messageId,
    priority: 'normal',
  });
}, { messageTypes: ['question', 'task_delegation'] });

bus.subscribe('writer-gmi', async (message) => {
  await bus.sendToAgent(message.fromAgentId, {
    type: 'answer',
    fromAgentId: 'writer-gmi',
    content: { accepted: true },
    inReplyTo: message.messageId,
    priority: 'normal',
  });
}, { messageTypes: ['task_delegation'] });

// Request/response: resolves with the researcher's answer.
const response = await bus.requestResponse('researcher-gmi', {
  type: 'question',
  fromAgentId: 'coordinator-gmi',
  content: 'What are the top three rollout risks?',
  priority: 'high',
  timeoutMs: 5_000,
});

// Handoff: a task_delegation request to the writer; an answer within
// 60 s accepts it and makes the writer the owner.
const handoff = await bus.handoff('researcher-gmi', 'writer-gmi', {
  taskId: 'rollout-note',
  taskDescription: 'Convert the findings into release-note prose',
  progress: 0.8,
  completedWork: ['Collected risks', 'Ranked findings'],
  remainingWork: ['Write final release note'],
  context: { audience: 'engineering' },
  reason: 'completion',
});

console.log(handoff.accepted, handoff.newOwnerId);  // true writer-gmi`,
    output: {
      busMessages: [
        {
          from: 'coordinator-gmi',
          to: 'researcher-gmi',
          status: 'success',
          preview: 'question: What are the top three rollout risks?',
        },
        {
          from: 'researcher-gmi',
          to: 'coordinator-gmi',
          status: 'success',
          preview:
            'answer: findings: ["The graph runtime should stay shared across all three authoring APIs.", "Checkpointing needs to be first-class, not bolted onto the planner.", "Examples should show workflow(), AgentGraph, and mission() side by side."]',
        },
        {
          from: 'researcher-gmi',
          to: 'writer-gmi',
          status: 'success',
          preview: 'task_delegation: rollout-note, Convert the findings into release-note prose',
        },
        {
          from: 'writer-gmi',
          to: 'researcher-gmi',
          status: 'success',
          preview: 'answer: { accepted: true }; handoff accepted, writer-gmi owns rollout-note',
        },
      ],
      usage: {},
    },
    caption: (
      <>
        Lower-level than <code className="font-mono text-[var(--color-accent-primary)]">agency()</code>: explicit per-agent IDs, role routing, request/response messages with structured content, and ownership handoff. Each agent replies through <code className="font-mono text-[var(--color-accent-primary)]">bus.subscribe()</code>; <code className="font-mono text-[var(--color-accent-primary)]">handoff()</code> sends the new owner a task_delegation request and accepts when an answer arrives within 60 seconds. Useful when an external orchestrator (a queue, a workflow engine, a UI) owns scheduling and wants AgentOS to carry the messages.
      </>
    ),
  },
  {
    id: 'high-level-api',
    title: 'Pick a provider, get its default model',
    exampleSlug: 'examples/high-level-api.mjs',
    language: 'typescript',
    code: `import { generateText, streamText, generateImage, agent } from '@framers/agentos';

// Pick a provider and AgentOS picks the default model.
// Reads OPENAI_API_KEY / ANTHROPIC_API_KEY / etc. from env.
const quick = await generateText({
  provider: 'openai',
  prompt: 'Explain what QUIC is in 3 concise bullet points.',
});
console.log(quick.text);

// Pin a specific model when you need it.
const pinned = await generateText({
  provider: 'openai',
  model: 'gpt-4o',
  prompt: 'What is TCP in one sentence?',
});

// Same options for streaming. streamText() returns its result
// synchronously, so it takes no await.
const stream = streamText({
  provider: 'openai',
  prompt: 'Compare QUIC vs TCP.',
});
for await (const chunk of stream.textStream) process.stdout.write(chunk);

// Same shape for image generation.
const img = await generateImage({
  provider: 'openai',
  prompt: 'Diagram of QUIC vs TCP, infographic style',
});

// agent() takes the provider and model with its instructions;
// generate() takes the prompt.
const researcher = agent({
  provider: 'openai',
  model: 'gpt-4o',
  instructions: 'You are a network protocols researcher.',
});
const result = await researcher.generate('What problems was QUIC designed to solve?');`,
    output: {
      finalAnswerLabel: 'generateText() — provider=openai, default model',
      finalAnswer: `- Transport Protocol: QUIC (Quick UDP Internet Connections) is a modern transport protocol developed by Google, designed to improve performance and security of internet connections by operating over UDP instead of TCP.

- Reduced Latency: It reduces latency by integrating features such as connection multiplexing, 0-RTT connection establishment, and forward error correction, which enhance speed and reliability for web and mobile applications.

- Enhanced Security: QUIC incorporates built-in encryption similar to TLS/SSL, ensuring secure data transmission over the network, with minimal latency incurred from cryptographic handshakes.`,
      usage: {},
    },
    caption: (
      <>
        Provider-first wrappers around the LLM call. <code className="font-mono text-[var(--color-accent-primary)]">generateText()</code>, <code className="font-mono text-[var(--color-accent-primary)]">streamText()</code> and <code className="font-mono text-[var(--color-accent-primary)]">generateImage()</code> take the same <code className="font-mono text-[var(--color-accent-primary)]">{`{ provider, model?, prompt }`}</code> options. <code className="font-mono text-[var(--color-accent-primary)]">agent()</code> takes the provider and model with its instructions, and its <code className="font-mono text-[var(--color-accent-primary)]">generate()</code> takes the prompt. Give a provider and AgentOS uses that provider&apos;s default model; set <code className="font-mono text-[var(--color-accent-primary)]">model:</code> when you need a specific one. The first three need no agent.
      </>
    ),
  },
]

function CopyCodeButton({ code }: { code: string }) {
  const { copied, copy } = useCopyToClipboard()
  return (
    <button
      type="button"
      onClick={() => copy(code)}
      aria-label={copied ? 'Copied' : 'Copy code'}
      className={
        'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium transition-colors ' +
        (copied
          ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
          : 'border-[var(--color-border-subtle)] bg-transparent text-[var(--color-text-secondary)] hover:border-[var(--color-border-interactive)] hover:text-[var(--color-text-primary)]')
      }
    >
      {copied ? (
        <>
          <Check className="h-3 w-3" />
          <span>Copied</span>
        </>
      ) : (
        <>
          <Copy className="h-3 w-3" />
          <span>Copy</span>
        </>
      )}
    </button>
  )
}

function ForgeBadge({ agent, approved, comment }: { agent: string; approved: boolean; comment?: string }) {
  return (
    <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 mb-4 dark:bg-emerald-500/10">
      <div className="font-mono text-[13px]">
        <span className="font-semibold text-emerald-600 dark:text-emerald-400">[FORGE]</span>{' '}
        spawned <span className="text-[var(--color-text-primary)]">&quot;{agent}&quot;</span>
      </div>
      <div className="ml-7 font-mono text-[12px] text-[var(--color-text-secondary)]">
        approved=<span className="text-emerald-700 dark:text-emerald-400">{String(approved)}</span>
      </div>
      {comment && (
        <div className="ml-7 mt-1 font-mono text-[11px] italic text-[var(--color-text-muted)]">
          {`// ${comment}`}
        </div>
      )}
    </div>
  )
}

function FinalAnswerBlock({ text, label }: { text: string; label?: string }) {
  return (
    <div className="mb-4">
      <div className="mb-1.5 font-mono text-[12px] font-semibold uppercase tracking-wider text-[var(--color-accent-primary)]">
        {label ?? 'Final answer (gpt-4o)'}
      </div>
      <div className="rounded-md border border-[var(--color-border-subtle)] bg-[var(--color-background-secondary)] p-3 text-[13px] leading-[1.65] text-[var(--color-text-primary)] whitespace-pre-wrap">
        {text}
      </div>
    </div>
  )
}

function DagTiersBlock({ tiers }: { tiers: NonNullable<DemoOutput['dagTiers']> }) {
  return (
    <div className="mb-4">
      <div className="mb-2 font-mono text-[12px] font-semibold uppercase tracking-wider text-[var(--color-accent-primary)]">
        Topo-sorted tiers
      </div>
      <div className="space-y-2">
        {tiers.map((t) => (
          <div
            key={t.tier}
            className="rounded-md border border-[var(--color-border-subtle)] bg-[var(--color-background-secondary)] p-3"
          >
            <div className="flex items-center gap-3 font-mono text-[12px]">
              <span className="rounded bg-[var(--color-background-tertiary)] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
                Tier {t.tier}
              </span>
              <span className="text-[var(--color-text-muted)]">
                {t.agents.length === 1 ? 'sequential' : `${t.agents.length} concurrent`}
              </span>
            </div>
            <div className="mt-1 ml-2 font-mono text-[12px] leading-[1.7]">
              {t.agents.map((a, i) => (
                <div key={i}>
                  <span className="text-[var(--color-accent-primary)]">→</span>{' '}
                  <span className="text-[var(--color-text-primary)]">{a.name}</span>
                  <span className="text-[var(--color-text-muted)]"> · {(a.durationMs / 1000).toFixed(2)}s</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function MissionStepsBlock({
  steps,
  artifacts,
  confidence,
}: {
  steps: NonNullable<DemoOutput['missionSteps']>
  artifacts: NonNullable<DemoOutput['missionArtifacts']>
  confidence: number
}) {
  return (
    <>
      <div className="mb-4">
        <div className="mb-2 font-mono text-[12px] font-semibold uppercase tracking-wider text-[var(--color-accent-primary)]">
          Generated step plan
        </div>
        <div className="rounded-md border border-[var(--color-border-subtle)] bg-[var(--color-background-secondary)] overflow-hidden">
          <table className="w-full font-mono text-[12px]">
            <thead className="bg-[var(--color-background-tertiary)] text-[var(--color-text-muted)]">
              <tr>
                <th className="text-left px-3 py-1.5 font-semibold">id</th>
                <th className="text-left px-3 py-1.5 font-semibold">type</th>
                <th className="text-left px-3 py-1.5 font-semibold">executor</th>
              </tr>
            </thead>
            <tbody>
              {steps.map((s) => (
                <tr key={s.id} className="border-t border-[var(--color-border-subtle)]">
                  <td className="px-3 py-1.5 text-[var(--color-text-primary)]">{s.id}</td>
                  <td className="px-3 py-1.5">
                    <span
                      className={
                        s.type === 'gmi'
                          ? 'text-[var(--color-accent-primary)]'
                          : 'text-amber-600 dark:text-amber-400'
                      }
                    >
                      {s.type}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-[var(--color-text-secondary)]">{s.executor}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="mb-4">
        <div className="mb-1.5 font-mono text-[12px] font-semibold uppercase tracking-wider text-[var(--color-accent-primary)]">
          Final artifacts <span className="text-[var(--color-text-muted)]">· confidence {(confidence * 100).toFixed(0)}%</span>
        </div>
        <pre className="rounded-md border border-[var(--color-border-subtle)] bg-[var(--color-background-secondary)] p-3 font-mono text-[12px] leading-[1.65] text-[var(--color-text-primary)] whitespace-pre-wrap">
{JSON.stringify(artifacts, null, 2)}
        </pre>
      </div>
    </>
  )
}

function BusMessagesBlock({ messages }: { messages: NonNullable<DemoOutput['busMessages']> }) {
  return (
    <div className="mb-4">
      <div className="mb-2 font-mono text-[12px] font-semibold uppercase tracking-wider text-[var(--color-accent-primary)]">
        Bus traffic
      </div>
      <div className="space-y-2">
        {messages.map((m, i) => (
          <div
            key={i}
            className="rounded-md border border-[var(--color-border-subtle)] bg-[var(--color-background-secondary)] p-3"
          >
            <div className="flex items-center gap-2 font-mono text-[12px]">
              <span className="text-[var(--color-text-secondary)]">{m.from}</span>
              <span className="text-[var(--color-accent-primary)]">→</span>
              <span className="text-[var(--color-text-primary)]">{m.to}</span>
              <span
                className={
                  'ml-auto rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wider ' +
                  (m.status === 'success'
                    ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30'
                    : 'bg-red-500/10 text-red-700 dark:text-red-400 border border-red-500/30')
                }
              >
                {m.status}
              </span>
            </div>
            <div className="mt-1.5 font-mono text-[11px] leading-[1.55] text-[var(--color-text-secondary)]">
              {m.preview}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function CorpusStatsBlock({ stats }: { stats: NonNullable<DemoOutput['corpusStats']> }) {
  return (
    <div className="mb-4">
      <div className="mb-2 font-mono text-[12px] font-semibold uppercase tracking-wider text-[var(--color-accent-primary)]">
        Corpus initialized
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="rounded-md border border-[var(--color-border-subtle)] bg-[var(--color-background-secondary)] p-3">
          <div className="font-mono text-[11px] uppercase tracking-wider text-[var(--color-text-muted)]">chunks</div>
          <div className="font-mono text-[18px] tabular-nums text-[var(--color-text-primary)]">
            {stats.chunks.toLocaleString()}
          </div>
        </div>
        <div className="rounded-md border border-[var(--color-border-subtle)] bg-[var(--color-background-secondary)] p-3">
          <div className="font-mono text-[11px] uppercase tracking-wider text-[var(--color-text-muted)]">topics</div>
          <div className="font-mono text-[18px] tabular-nums text-[var(--color-text-primary)]">
            {stats.topics}
          </div>
        </div>
        <div className="rounded-md border border-[var(--color-border-subtle)] bg-[var(--color-background-secondary)] p-3">
          <div className="font-mono text-[11px] uppercase tracking-wider text-[var(--color-text-muted)]">sources</div>
          <div className="font-mono text-[18px] tabular-nums text-[var(--color-text-primary)]">
            {stats.sources}
          </div>
        </div>
      </div>
      <div className="rounded-md border border-[var(--color-border-subtle)] bg-[var(--color-background-secondary)] p-3">
        <div className="mb-1.5 font-mono text-[11px] uppercase tracking-wider text-[var(--color-text-muted)]">
          Platform knowledge entries
        </div>
        <div className="grid grid-cols-5 gap-2 font-mono text-[12px]">
          {Object.entries(stats.platform).map(([k, v]) => (
            <div key={k} className="flex flex-col">
              <span className="text-[var(--color-text-muted)]">{k}</span>
              <span className="text-[var(--color-text-primary)] tabular-nums">{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function StreamingBlock({ preface, finalText }: { preface: string; finalText: string }) {
  return (
    <>
      <div className="mb-4">
        <div className="mb-1.5 font-mono text-[12px] font-semibold uppercase tracking-wider text-[var(--color-accent-primary)]">
          Live token stream (raw)
        </div>
        <div className="rounded-md border border-[var(--color-border-subtle)] bg-[var(--color-background-secondary)] p-3 text-[13px] leading-[1.6] text-[var(--color-text-primary)] whitespace-pre-wrap max-h-72 overflow-y-auto">
          {preface}
          <span className="inline-block ml-1 align-middle h-3.5 w-1.5 bg-[var(--color-accent-primary)] animate-pulse" />
        </div>
      </div>
      <div className="mb-4">
        <div className="mb-1.5 font-mono text-[12px] font-semibold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
          HITL gate → approved final
        </div>
        <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3 font-mono text-[13px] leading-[1.65] text-[var(--color-text-primary)] whitespace-pre-wrap">
          {finalText}
        </div>
      </div>
    </>
  )
}

/** The mark shown beside a claim, by the verifier's verdict. */
const VERDICT_MARKS: Record<NonNullable<DemoOutput['claims']>[number]['verdict'], { mark: string; className: string }> = {
  supported: { mark: '✓', className: 'text-emerald-600 dark:text-emerald-400' },
  weak: { mark: '~', className: 'text-amber-600 dark:text-amber-400' },
  unverifiable: { mark: '?', className: 'text-[var(--color-text-muted)]' },
}

function ClaimsBlock({ claims }: { claims: NonNullable<DemoOutput['claims']> }) {
  const supportedCount = claims.filter((c) => c.verdict === 'supported').length
  return (
    <div className="mb-4">
      <div className="mb-2 font-mono text-[12px] font-semibold uppercase tracking-wider text-[var(--color-accent-primary)]">
        Verification result · {supportedCount}/{claims.length} supported
      </div>
      <div className="space-y-2">
        {claims.map((c, i) => (
          <div
            key={i}
            className="rounded-md border border-[var(--color-border-subtle)] bg-[var(--color-background-secondary)] p-3"
          >
            <div className="flex items-start gap-2 font-mono text-[13px]">
              <span className={`font-semibold ${VERDICT_MARKS[c.verdict].className}`}>{VERDICT_MARKS[c.verdict].mark}</span>
              <span className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] mt-1">
                {c.verdict}
              </span>
              <span className="text-[var(--color-text-muted)]">·</span>
              <span className="font-mono text-[12px] tabular-nums text-amber-600 dark:text-amber-400">
                {(c.confidence * 100).toFixed(0)}%
              </span>
              <span className="flex-1 text-[var(--color-text-primary)]">{c.text}</span>
            </div>
            {c.source && (
              <div className="mt-1 ml-5 font-mono text-[11px] text-[var(--color-text-muted)] italic line-clamp-2">
                source: {c.source}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

function AgentCallsBlock({ calls }: { calls: AgentCall[] }) {
  return (
    <div className="mb-3">
      <div className="mb-1 font-mono text-[12px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
        Agent calls ({calls.length})
      </div>
      <div className="font-mono text-[12px] leading-[1.7] text-[var(--color-text-secondary)]">
        {calls.map((c, i) => (
          <div key={i}>
            <span className="text-[var(--color-accent-primary)]">→</span>{' '}
            <span className="text-[var(--color-text-primary)]">{c.agent}:</span>{' '}
            <span>&quot;{c.input}&quot;</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function UsageBlock({ usage }: { usage: DemoOutput['usage'] }) {
  if (!usage.tokens && !usage.cost && !usage.latency) return null
  return (
    <div className="font-mono text-[12px] text-[var(--color-text-secondary)] tabular-nums">
      {usage.tokens !== undefined && (
        <span>
          tokens: <span className="text-[var(--color-text-primary)]">{usage.tokens.toLocaleString()}</span>
        </span>
      )}
      {usage.cost && (
        <span className="ml-4">
          cost: <span className="text-[var(--color-text-primary)]">{usage.cost}</span>
        </span>
      )}
      {usage.latency && (
        <span className="ml-4">
          latency: <span className="text-[var(--color-text-primary)]">{usage.latency}</span>
        </span>
      )}
    </div>
  )
}

function OutputPanel({ demo }: { demo: DemoData }) {
  const { output } = demo
  return (
    <div className="group/panel relative overflow-hidden rounded-2xl border border-[var(--color-border-subtle)] bg-[var(--color-background-elevated)]/70 backdrop-blur-xl shadow-[0_8px_30px_-12px_rgba(0,0,0,0.15)] dark:shadow-[0_8px_30px_-12px_rgba(0,0,0,0.6)] ring-1 ring-[var(--color-accent-primary)]/5 transition-all duration-300 hover:ring-[var(--color-accent-primary)]/15 hover:shadow-[0_12px_40px_-12px_rgba(0,0,0,0.25)] dark:hover:shadow-[0_12px_40px_-12px_rgba(0,0,0,0.7)]">
      {/* Top accent gradient line — subtle but signals "this is the live
          surface" without screaming. Stronger on the right where the
          status badge sits. */}
      <div
        className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[var(--color-accent-primary)]/40 to-emerald-400/60"
        aria-hidden="true"
      />
      <div className="flex items-center justify-between border-b border-[var(--color-border-subtle)] bg-[var(--color-background-tertiary)]/50 backdrop-blur-sm px-4 py-2.5">
        <div className="flex items-center gap-3">
          {/* Real macOS traffic-light dots — subtle nostalgia, evokes
              "this is a terminal session" without the gray-pellet look. */}
          <div className="flex gap-1.5">
            <div className="h-2.5 w-2.5 rounded-full bg-[#ff5f57] shadow-[inset_0_0_0_0.5px_rgba(0,0,0,0.15)]" />
            <div className="h-2.5 w-2.5 rounded-full bg-[#febc2e] shadow-[inset_0_0_0_0.5px_rgba(0,0,0,0.15)]" />
            <div className="h-2.5 w-2.5 rounded-full bg-[#28c840] shadow-[inset_0_0_0_0.5px_rgba(0,0,0,0.15)]" />
          </div>
          <span className="font-mono text-xs text-[var(--color-text-secondary)]">
            output of one run
          </span>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
          <span className="relative flex h-1.5 w-1.5" aria-hidden="true">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
          </span>
          Output
        </span>
      </div>
      <div className="px-5 py-4">
        {output.demoGif && (
          <figure className="mb-4 -mx-1 overflow-hidden rounded-lg border border-[var(--color-border-subtle)] bg-black/40">
            {output.demoGif.mp4Src ? (
              // Heavy animated demos ship as a muted, looping, inline <video>
              // (H.264 MP4, ~60% smaller than the old animated WebP/GIF).
              // preload="none" keeps it off the initial-load critical path.
              <video
                autoPlay
                loop
                muted
                playsInline
                preload="none"
                poster={output.demoGif.poster}
                width={output.demoGif.width}
                height={output.demoGif.height}
                aria-label={output.demoGif.alt}
                className="w-full h-auto block"
              >
                <source src={output.demoGif.mp4Src} type="video/mp4" />
                {/* MP4 is the animated source (universally supported). The
                    <video> fallback is a STATIC poster/WebP, never the heavy
                    animated GIF — fetching that as fallback cost ~2.5MB. */}
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={output.demoGif.poster ?? output.demoGif.webpSrc ?? output.demoGif.src}
                  alt={output.demoGif.alt}
                  width={output.demoGif.width}
                  height={output.demoGif.height}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-auto block"
                />
              </video>
            ) : (
              <picture>
                {output.demoGif.webpSrc && (
                  <source srcSet={output.demoGif.webpSrc} type="image/webp" />
                )}
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={output.demoGif.src}
                  alt={output.demoGif.alt}
                  width={output.demoGif.width}
                  height={output.demoGif.height}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-auto block"
                />
              </picture>
            )}
          </figure>
        )}
        {output.forge && <ForgeBadge {...output.forge} />}
        {output.dagTiers && <DagTiersBlock tiers={output.dagTiers} />}
        {output.missionSteps && output.missionArtifacts && (
          <MissionStepsBlock
            steps={output.missionSteps}
            artifacts={output.missionArtifacts}
            confidence={output.missionConfidence ?? 0}
          />
        )}
        {output.busMessages && <BusMessagesBlock messages={output.busMessages} />}
        {output.corpusStats && <CorpusStatsBlock stats={output.corpusStats} />}
        {output.streamPreface && output.finalOverride && (
          <StreamingBlock preface={output.streamPreface} finalText={output.finalOverride} />
        )}
        {output.finalAnswer && !output.streamPreface && (
          <FinalAnswerBlock text={output.finalAnswer} label={output.finalAnswerLabel} />
        )}
        {output.claims && <ClaimsBlock claims={output.claims} />}
        {output.agentCalls && <AgentCallsBlock calls={output.agentCalls} />}
        <UsageBlock usage={output.usage} />
      </div>
    </div>
  )
}

export function LiveRunDemoSection() {
  const { resolvedTheme } = useTheme()
  const locale = useLocale()
  const [activeId, setActiveId] = useState(demos[0].id)
  const active = demos.find((d) => d.id === activeId) ?? demos[0]
  const codeStyle = useMemo(() => (resolvedTheme === 'light' ? oneLight : atomDark), [resolvedTheme])

  return (
    <section className="relative mx-auto max-w-7xl px-6 py-24 md:py-32">
      {/* Ambient backdrop — soft accent-tinted radial behind the panels +
          a faint dot grid that catches the light without competing for
          attention. Both layers fade into the section background; nothing
          sits in front of content. */}
      <div
        className="pointer-events-none absolute inset-0 -z-10 overflow-hidden"
        aria-hidden="true"
      >
        <div className="absolute left-1/2 top-1/3 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle_at_center,_var(--color-accent-primary)_0%,_transparent_60%)] opacity-[0.06] blur-3xl" />
        <div className="absolute inset-0 opacity-[0.04] bg-[radial-gradient(circle,_var(--color-text-muted)_1px,_transparent_1px)] bg-[length:24px_24px]" />
      </div>

      <div className="mb-8 max-w-3xl">
        {/* Verified-runs badge with pulsing indicator — signals the section is
            live captures, not synthetic snippets. Sets the credibility tone
            before the heading lands. */}
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 backdrop-blur-md shadow-[0_0_20px_-8px_rgba(16,185,129,0.4)]">
          <span className="relative flex h-2 w-2" aria-hidden="true">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
          </span>
          <span className="font-mono text-[11px] font-medium uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
            Runnable examples
          </span>
        </div>
        <h2 className="text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl text-[var(--color-text-primary)]">
          Real output from{' '}
          <span className="bg-gradient-to-r from-[var(--color-text-primary)] via-[var(--color-accent-primary)] to-[var(--color-text-primary)] bg-clip-text text-transparent">
            real scripts
          </span>
        </h2>
        <p className="mt-3 text-base text-[var(--color-text-secondary)]">
          Each tab pairs code based on a script in{' '}
          <a
            className="underline underline-offset-4 hover:opacity-80"
            href="https://github.com/framerslab/agentos/tree/master/examples"
            target="_blank"
            rel="noopener noreferrer"
          >
            framerslab/agentos/examples
          </a>
          {' '}with output from a run of that code. Tabs that call a model ran against the OpenAI API; the mission and bus examples call no model, so every run takes the same steps. Pick one.
        </p>
      </div>

      {/* Tab strip — horizontally scrollable on overflow with edge-fade
          gradients so users perceive the overflow on narrow viewports. */}
      <div className="relative mb-6 -mx-4 sm:mx-0">
        {/* Left edge fade — only visible on horizontal-scroll viewports */}
        <div
          className="pointer-events-none absolute inset-y-0 left-0 z-10 w-10 bg-gradient-to-r from-[var(--color-background-primary)] via-[var(--color-background-primary)]/80 to-transparent sm:hidden"
          aria-hidden="true"
        />
        {/* Right edge fade */}
        <div
          className="pointer-events-none absolute inset-y-0 right-0 z-10 w-10 bg-gradient-to-l from-[var(--color-background-primary)] via-[var(--color-background-primary)]/80 to-transparent sm:hidden"
          aria-hidden="true"
        />
        <div className="overflow-x-auto px-4 sm:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <div className="flex gap-2 min-w-max sm:flex-wrap sm:min-w-0">
            {demos.map((d, i) => (
              <button
                key={d.id}
                onClick={() => setActiveId(d.id)}
                aria-pressed={d.id === activeId}
                className={
                  'group inline-flex shrink-0 items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200 ' +
                  (d.id === activeId
                    ? 'border border-[var(--color-border-interactive)] bg-[var(--color-background-elevated)] text-[var(--color-text-primary)] shadow-sm ring-1 ring-[var(--color-accent-primary)]/25'
                    : 'border border-transparent bg-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-background-secondary)]')
                }
              >
                <span
                  className={
                    'font-mono text-[11px] tabular-nums transition-colors ' +
                    (d.id === activeId
                      ? 'text-[var(--color-accent-primary)] font-semibold'
                      : 'text-[var(--color-text-muted)] group-hover:text-[var(--color-text-secondary)]')
                  }
                >
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span>{d.title}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Side-by-side panels */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Code panel — matches the output panel's chrome (glass, accent
            top line, real macOS dots, hover ring) so the pair reads as
            one composed surface, not two stacked rectangles. */}
        <div className="group/panel relative overflow-hidden rounded-2xl border border-[var(--color-border-subtle)] bg-[var(--color-background-elevated)]/70 backdrop-blur-xl shadow-[0_8px_30px_-12px_rgba(0,0,0,0.15)] dark:shadow-[0_8px_30px_-12px_rgba(0,0,0,0.6)] ring-1 ring-[var(--color-accent-primary)]/5 transition-all duration-300 hover:ring-[var(--color-accent-primary)]/15 hover:shadow-[0_12px_40px_-12px_rgba(0,0,0,0.25)] dark:hover:shadow-[0_12px_40px_-12px_rgba(0,0,0,0.7)]">
          <div
            className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-[var(--color-accent-primary)]/60 via-[var(--color-accent-primary)]/30 to-transparent"
            aria-hidden="true"
          />
          <div className="flex items-center justify-between border-b border-[var(--color-border-subtle)] bg-[var(--color-background-tertiary)]/50 backdrop-blur-sm px-4 py-2.5">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex gap-1.5 shrink-0">
                <div className="h-2.5 w-2.5 rounded-full bg-[#ff5f57] shadow-[inset_0_0_0_0.5px_rgba(0,0,0,0.15)]" />
                <div className="h-2.5 w-2.5 rounded-full bg-[#febc2e] shadow-[inset_0_0_0_0.5px_rgba(0,0,0,0.15)]" />
                <div className="h-2.5 w-2.5 rounded-full bg-[#28c840] shadow-[inset_0_0_0_0.5px_rgba(0,0,0,0.15)]" />
              </div>
              <span className="font-mono text-xs text-[var(--color-text-secondary)] truncate">
                {active.adapted ? 'adapted from' : 'based on'} {active.exampleSlug}
              </span>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="rounded-full border border-[var(--color-border-subtle)] bg-[var(--color-background-secondary)]/60 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
                JavaScript
              </span>
              <CopyCodeButton code={active.code} />
            </div>
          </div>
          <SyntaxHighlighter
            language={active.language}
            style={codeStyle}
            customStyle={{
              margin: 0,
              padding: '18px 20px',
              background: 'transparent',
              fontSize: '13px',
              lineHeight: '1.65',
            }}
            codeTagProps={{ style: { fontFamily: 'JetBrains Mono, Menlo, Consolas, monospace' } }}
          >
            {active.code}
          </SyntaxHighlighter>
        </div>

        {/* Output panel */}
        <OutputPanel demo={active} />
      </div>

      {/* Caption — border-left callout treatment to set the explanation
          apart from the surrounding panels. */}
      <div className="mt-6 max-w-3xl border-l-2 border-[var(--color-accent-primary)]/40 pl-4">
        <p className="text-sm leading-relaxed text-[var(--color-text-secondary)]">
          {active.caption}
        </p>
      </div>

      {/* CTAs — clear three-tier visual hierarchy:
          1. Primary action  (solid, branded)
          2. Install snippet (outlined, mono)
          3. Tertiary links  (text-only, arrow-suffixed)
          A subtle divider between secondary and tertiary so the eye reads
          "act now / install / read more" without scanning every button. */}
      <div className="mt-8 flex flex-wrap items-center gap-3">
        <a
          href={`https://github.com/framerslab/agentos/blob/master/${active.exampleSlug}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-text-primary)] px-5 py-2.5 text-sm font-semibold text-[var(--color-background-primary)] shadow-sm transition-all hover:opacity-90 hover:shadow-md"
        >
          <Github className="h-4 w-4" />
          Source on GitHub
        </a>
        <a
          href="https://www.npmjs.com/package/@framers/agentos"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-background-elevated)] px-5 py-2.5 font-mono text-xs font-medium text-[var(--color-text-primary)] transition-colors hover:border-[var(--color-border-interactive)] hover:bg-[var(--color-background-secondary)]"
        >
          npm install @framers/agentos
        </a>
        {/* Tier divider — visible on wide layouts only */}
        <span
          className="hidden h-6 w-px bg-[var(--color-border-subtle)] sm:block"
          aria-hidden="true"
        />
        <a
          href={`/${locale}/blog/agentos-memory-sota-longmemeval`}
          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2.5 text-sm font-medium text-[var(--color-text-secondary)] transition-all hover:text-[var(--color-text-primary)] hover:translate-x-0.5"
        >
          85.6% on LongMemEval-S
          <span aria-hidden="true">→</span>
        </a>
        <a
          href="https://github.com/framerslab/agentos/tree/master/examples"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2.5 text-sm font-medium text-[var(--color-text-secondary)] transition-all hover:text-[var(--color-text-primary)] hover:translate-x-0.5"
        >
          Browse the example scripts
          <span aria-hidden="true">→</span>
        </a>
      </div>
    </section>
  )
}
